/**
 * One presentational state chip for a checklist side (tenant or landlord), shared by the lease
 * banner, the lease Checklists tab, the Checklists page and the read-only modal. Built on the
 * `Badge` variants (success / primary / secondary) — no hand-rolled chips.
 */

import { AlertTriangle, Check, CircleDashed, Clock } from 'lucide-react';
import { asIsoDateTimeString, formatTimestamp } from '@resida/shared';
import { Badge } from '../ui/badge';
import type { ChecklistState } from '../../lib/condition.api';

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export interface ChecklistStatusBadgeProps {
  /** `ERROR` = the status could not be loaded; never guessed as "Not started". */
  state: ChecklistState | 'ERROR';
  submittedAt?: string | null;
  /** Tenant move-out with nothing yet is optional (BR-5) — neutral, never a warning. */
  optional?: boolean;
  /** The landlord's own side reads "Done" once submitted. */
  side?: 'tenant' | 'landlord';
  /** Drop the year from the submitted date (table rows). */
  shortDate?: boolean;
  'data-testid'?: string;
}

function submittedLabel(submittedAt: string | null | undefined, shortDate: boolean): string {
  if (!submittedAt) return 'Submitted';
  const date = formatTimestamp(
    asIsoDateTimeString(submittedAt),
    LOCAL_TZ,
    shortDate ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' },
  );
  return `Submitted · ${date}`;
}

export function ChecklistStatusBadge({
  state,
  submittedAt,
  optional = false,
  side = 'tenant',
  shortDate = false,
  'data-testid': testId,
}: ChecklistStatusBadgeProps) {
  const iconProps = { className: 'mr-1 h-3.5 w-3.5 shrink-0', 'aria-hidden': true } as const;

  if (state === 'SUBMITTED') {
    return (
      <Badge variant="success" className="w-fit" data-testid={testId}>
        <Check {...iconProps} />
        {side === 'landlord' ? 'Done' : submittedLabel(submittedAt, shortDate)}
      </Badge>
    );
  }
  if (state === 'IN_PROGRESS') {
    return (
      <Badge variant="primary" className="w-fit" data-testid={testId}>
        <Clock {...iconProps} />
        In progress
      </Badge>
    );
  }
  if (state === 'ERROR') {
    return (
      <Badge variant="secondary" className="w-fit" data-testid={testId}>
        <AlertTriangle {...iconProps} />
        Status unavailable
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="w-fit" data-testid={testId}>
      <CircleDashed {...iconProps} />
      {side === 'tenant' && optional ? 'Optional — not started' : 'Not started'}
    </Badge>
  );
}
